import * as React from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useParams } from 'react-router-dom';
import DownloadHint from './DownloadHint';

const _lang = 'es';
const words = {
  es: {
    subtitle: 'Selecciona tu sistema operativo e instala Klarway SafeGuard Service',
    download: 'DESCARGAR',
    notFoundTitle: 'Página no encontrada',
    notFoundDescription: 'La URL ingresada no es válida o ya no existe. Por favor, verifica la dirección e inténtalo nuevamente.',
    downloadHint: {
      title: 'Descarga iniciada',
      description: 'Busca el instalador en el icono de descargas de Chrome, arriba a la derecha.',
      instruction: 'Al ubicar el archivo, haz clic en él para instalarlo.',
      blockedMessage: 'Si Chrome bloquea la apertura automática, presiona',
      viewDownloads: 'Ver descargas',
      close: 'Cerrar',
    },
  },
  en: {
    subtitle: 'Choose your OS and install Klarway SafeGuard Service on your device.',
    download: 'DOWNLOAD',
    notFoundTitle: 'Page not found',
    notFoundDescription: 'The URL you entered is not valid or no longer exists. Please check the address and try again.',
    downloadHint: {
      title: 'Download started',
      description: 'Find the installer in Chrome downloads, at the top right of the browser.',
      instruction: 'Once you find the file, click on it to install.',
      blockedMessage: 'If Chrome blocks the automatic opening, press',
      viewDownloads: 'View downloads',
      close: 'Close',
    },
  },
};

export default function Service() {
  const params = useParams();
  const [defaultOS, setOs] = React.useState(null);
  const [enviromentKW, setEnv] = React.useState(null);
  const [downloads, setDownloads] = React.useState({ mac: null, win: null });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showDownloadHint, setShowDownloadHint] = React.useState(false);

  const getOS = () => {
    switch (defaultOS) {
      case 1:
        return 'windows';
      case 2:
        return 'macos';
      default:
        return 'windows';
    }
  };

  const getDownloadLink = () => (getOS() === 'windows' ? downloads.win : downloads.mac);

  const resolveBaseDomain = (env) => {
    if (!env) return 'https://generals-devs.klarway.com';
    const suffix = env.toLowerCase();
    if (suffix.endsWith('-devs')) return 'https://generals-devs.klarway.com';
    if (suffix.endsWith('-qa')) return 'https://generals-qa.klarway.com';
    if (suffix.endsWith('-uat')) return 'https://generals-uat.klarway.com';
    if (suffix.endsWith('-rc')) return 'https://generals-rc.klarway.com';
    if (suffix.endsWith('-expo')) return 'https://generals-expo.klarway.com';
    if (suffix.endsWith('-experience')) return 'https://generals-experience.klarway.com';
    if (suffix.endsWith('-release')) return 'https://generals.klarway.com';
    return 'https://generals-devs.klarway.com';
  };

  React.useEffect(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const envFromPath = params?.env || pathParts[1] || '';
    const cleanedEnv = envFromPath.trim();
    setEnv(cleanedEnv || null);
    if (!cleanedEnv) {
      setLoading(false);
    }
    console.log('[Service] Entorno detectado:', cleanedEnv || 'none');

    if (navigator.platform.indexOf('Win') !== -1) {
      setOs(1);
    } else if (navigator.platform.indexOf('Mac') !== -1) {
      setOs(2);
    }
  }, [params?.env]);

  React.useEffect(() => {
    if (!enviromentKW) return;
    const controller = new AbortController();
    const fetchDownloads = async () => {
      try {
        setLoading(true);
        setError(null);
        const baseDomain = resolveBaseDomain(enviromentKW);
        const endpoint = `${baseDomain}/download/products/service/${enviromentKW}`;
        console.log('[Service] Endpoint armado:', endpoint);
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: { Agent: 'Klarway-panel' },
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        console.log('[Service] Respuesta download:', data);
        const macUrl = data?.url_download?.service_mac || data?.data?.url_download?.service_mac || null;
        const winUrl = data?.url_download?.service_win || data?.data?.url_download?.service_win || null;
        console.log('[Service] Enlaces obtenidos:', { macUrl, winUrl });
        const successFlag = data?.success ?? data?.data?.success;
        const hasLinks = Boolean(macUrl || winUrl);
        if (successFlag === false || !hasLinks) {
          throw new Error('Env no válido o sin enlaces');
        }
        setDownloads({ mac: macUrl, win: winUrl });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('No pudimos obtener los enlaces de descarga. Revisa tu url o consulta con tu institución.');
          console.error('[Service] Error obteniendo enlaces:', err);
          setDownloads({ mac: null, win: null });
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchDownloads();
    return () => controller.abort();
  }, [enviromentKW]);

  React.useEffect(() => {
    console.log('[Service] Estado:', {
      entorno: enviromentKW,
      soSeleccionado: getOS(),
      loading,
      downloads,
        enlaceActual: getDownloadLink(),
    });
  }, [enviromentKW, defaultOS, loading, downloads]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#ffffff',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#009EFD' }} />
      </Box>
    );
  }

  const renderNotFound = () => (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 2,
      }}
    >
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Box
          component="img"
          src={process.env.PUBLIC_URL + '/errpag.png'}
          alt="Not found"
          sx={{ width: 140, height: 60, objectFit: 'contain' }}
        />
        <Typography variant="h5" component="h1">
          {words[_lang].notFoundTitle}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
          {words[_lang].notFoundDescription}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 5 }}>
        Si necesitas ayuda, comunícate con tu institución o{' '}
        <a href="https://ayuda.klarway.com/pagina-de-ayuda-de-klarway/" target="_blank" rel="noreferrer" style={{ color: '#009EFD' }}>
          ingresa aquí
        </a>
        .
      </Typography>
    </Box>
  );

  if (!enviromentKW || error) {
    return renderNotFound();
  }

  return (
    <React.Fragment>
      <Grid display="flex" alignItems="center">
        <Grid
          align="center"
          height="100vh"
          width="50%"
          sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
        >
          <Button disableRipple disableFocusRipple disabled sx={{ mb: 2 }}>
            <img
              src={process.env.PUBLIC_URL + '/kgris.svg'}
              width="50%"
              height="70%"
              style={{ margin: '10px' }}
              alt="Klarway"
            />
          </Button>
          <Typography variant="h5" align="center" color="text.secondary" component="p" sx={{ maxWidth: 420 }}>
            {words[_lang].subtitle}
          </Typography>
          <Grid display="grid" align="center" paddingTop={5} gap={1.5}>
            <Button
              onClick={() => {
                setOs(1);
              }}
              style={{
                border: defaultOS === 1 ? 'solid 2px' : 'solid 1px',
                borderColor: defaultOS === 1 ? '#049dfb' : '#c8c8c8',
                color: defaultOS === 1 ? '#049dfb' : '#000000',
                height: '70%',
                width: '50%',
                margin: 'auto',
              }}
            >
              <img
                src={defaultOS === 1 ? process.env.PUBLIC_URL + '/winlogo1.png' : process.env.PUBLIC_URL + '/winlogo.png'}
                width="5%"
                style={{ margin: '10px' }}
                alt="Windows"
              />{' '}
              <h3>Windows</h3>
            </Button>
            <Button
              onClick={() => {
                setOs(2);
              }}
              style={{
                border: defaultOS === 2 ? 'solid 2px' : 'solid 1px',
                borderColor: defaultOS === 2 ? '#049dfb' : '#c8c8c8',
                color: defaultOS === 2 ? '#049dfb' : '#000000',
                height: '70%',
                width: '50%',
                margin: 'auto',
              }}
            >
              <img
                src={defaultOS === 2 ? process.env.PUBLIC_URL + '/ioslogo1.png' : process.env.PUBLIC_URL + '/ioslogo.png'}
                width="5%"
                style={{ margin: '10px' }}
                alt="macOS"
              />{' '}
              <h3>macOS</h3>
            </Button>
            <Button
              onClick={() => {
                const link = getDownloadLink();
                console.log('Downloading from link:', link);
                if (link) {
                  window.open(link);
                  setShowDownloadHint(true);
                }
              }}
              style={{
                borderColor: '#049dfb',
                color: '#fff',
                backgroundColor: '#049dfb',
                height: '70%',
                width: '50%',
                margin: 'auto',
                marginTop: '10px',
              }}
              disabled={loading || !getDownloadLink()}
            >
              <h3>{words[_lang].download}</h3>
            </Button>
            {loading && (
              <Grid align="center" sx={{ mt: 2 }}>
                <CircularProgress size={28} />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid
          width="50%"
          style={{
            height: '100vh',
            background: `url('${process.env.PUBLIC_URL + '/pcinicio.png'}')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </Grid>
      <DownloadHint open={showDownloadHint} onClose={() => setShowDownloadHint(false)} words={words[_lang].downloadHint} />
    </React.Fragment>
  );
}
